import { Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef } from "react";
import { Redirect } from "react-router-dom";

import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { invalidateShifts } from "../../invalidateShifts";
import { useShiftBundles } from "../../Shift/Bundle/useShiftBundles";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { VirtualShiftListContextProvider } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { PullToRefresh } from "../ListView/PullToRefresh";
import {
  SHIFT_DISCOVERY_BONUS_ELIGIBLE_SHIFT_SHOPPING_PATH,
  SHIFT_DISCOVERY_LIST_PATH,
} from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { DEFAULT_BONUS_ELIGIBLE_SHIFT_SHOPPING_CONFIG } from "./constants";
import { BonusEligibleShiftShoppingList } from "./List";
import { useBonusEligibleShiftShoppingData } from "./useBonusEligibleShiftShoppingData";

export function ShiftDiscoveryBonusEligibleShiftShoppingPage() {
  const queryClient = useQueryClient();
  const stackRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(stackRef);

  const { userId } = useDefinedWorker();
  const { setBaseNavigationPathOverride } = useShiftModalsDataContext();

  const bonusEligibleShiftShoppingConfig = useCbhFlag(
    CbhFeatureFlag.BONUS_ELIGIBLE_SHIFT_SHOPPING,
    {
      defaultValue: DEFAULT_BONUS_ELIGIBLE_SHIFT_SHOPPING_CONFIG,
    }
  );

  const {
    enabled,
    bonusAmountInDollars,
    requiredShiftCount,
    deadlineDate,
    startDate,
    minimumPostedHours,
  } = bonusEligibleShiftShoppingConfig;

  useEffect(() => {
    setBaseNavigationPathOverride(SHIFT_DISCOVERY_BONUS_ELIGIBLE_SHIFT_SHOPPING_PATH);
    return () => {
      setBaseNavigationPathOverride(undefined);
    };
  }, [setBaseNavigationPathOverride]);

  useEffect(() => {
    void invalidateShifts(queryClient);
  }, [queryClient]);

  const {
    shifts: bonusEligibleShifts,
    allShifts,
    isError,
    isLoading,
    isSuccess,
    workplacesMap,
    rateNegotiationsMap,
    shiftAuctionsMap,
    pagination,
  } = useBonusEligibleShiftShoppingData({ minimumPostedHours });

  useLogEffect(
    APP_V2_USER_EVENTS.BONUS_ELIGIBLE_SHIFT_SHOPPING_PAGE_VIEWED,
    {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
      shiftsCount: bonusEligibleShifts.length,
    },
    { enabled: isSuccess }
  );

  const eligibleWorkplaceIds = useMemo(() => {
    const eligibleIds = new Set<string>();
    if (!workplacesMap) {
      return eligibleIds;
    }

    for (const [workplaceId, workplace] of workplacesMap) {
      if (workplace.attributes.isShiftBundlesEnabled) {
        eligibleIds.add(workplaceId);
      }
    }

    return eligibleIds;
  }, [workplacesMap]);

  const bundlesByShiftId = useShiftBundles(allShifts, {
    eligibleWorkplaceIds,
  });

  if (!enabled) {
    return <Redirect to={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`} />;
  }

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        backgroundColor: theme.palette.background.tertiary,
        overflow: "hidden",
        position: "relative",
      })}
    >
      <OpenShiftListDataProvider
        openShifts={bonusEligibleShifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={rateNegotiationsMap}
        shiftAuctionsMap={shiftAuctionsMap}
        bundlesByShiftId={bundlesByShiftId}
        isOpenShiftsLoading={isLoading}
        isOpenShiftsError={isError}
        isOpenShiftsSuccess={isSuccess}
        canLoadMoreShifts={pagination?.hasNextPage ?? false}
        isLoadingMoreShifts={pagination?.isFetchingNextPage ?? false}
        loadMoreShifts={() => {
          void pagination?.fetchNextPage();
        }}
      >
        <VirtualShiftListContextProvider shifts={bonusEligibleShifts} workplacesMap={workplacesMap}>
          <PullToRefresh
            scrollRef={stackRef}
            onRefresh={async () => {
              await invalidateShifts(queryClient);
            }}
          />

          <StickyScrollPageHeader
            title={`${bonusEligibleShifts.length > 0 ? `${bonusEligibleShifts.length} ` : ""}Bonus-Eligible ${pluralize(
              "Shift",
              bonusEligibleShifts.length
            )}`}
            headerVariant="tertiary"
            scrollContainerRef={stackRef}
            fallbackGoBackPath={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`}
          />

          <Stack
            ref={stackRef}
            data-testid="bonus-eligible-shift-shopping-page-scroll-container"
            spacing={7}
            sx={{
              flex: 1,
              overflow: "auto",
              paddingX: 5,
              paddingBottom: 6,
            }}
          >
            <Text
              semibold
              variant="body2"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                paddingTop: 2,
              })}
            >
              {`Complete ${requiredShiftCount} of the shifts below between ${startDate} and ${deadlineDate} to earn $${bonusAmountInDollars}.`}
            </Text>

            <BonusEligibleShiftShoppingList scrollRef={stackRef} />
          </Stack>
        </VirtualShiftListContextProvider>
      </OpenShiftListDataProvider>
    </Stack>
  );
}
